/**
 * Client for the registration and documents endpoints (api/v1/precheckin/*). They used to be a
 * separate pre-check-in form with its own short-lived token; they are now portal modules, called with
 * the guest's normal portal login — the stay session is the same one from booking to checkout.
 *
 * Calls go to the configured API host (VITE_API_URL, e.g. https://api.myroomhq.com), NOT a
 * relative /api path — the guest domain doesn't proxy /api to the backend in production.
 */

const API_BASE = import.meta.env.VITE_API_URL || '';
const url = (path: string) => `${API_BASE}${path}`;

export interface PreCheckInState {
  reservationId: string;
  status: string;
  guestFirstName?: string | null;
  guestLastName?: string | null;
  email?: string | null;
  phoneE164?: string | null;
  country?: string | null;
  adults: number;
  children: number;
  babies: number;
  checkInDate: string;
  checkOutDate: string;
  detailsComplete: boolean;
  confirmComplete: boolean;
  percentComplete: number;
}

export interface PreCheckInDetailsPayload {
  firstName: string;
  lastName: string;
  email?: string;
  phoneE164?: string;
  country?: string;
  passportId?: string;
  dateOfBirth?: string | null;
  adults?: number;
  children?: number;
  babies?: number;
}

/** The portal login; the old form's token only as a fallback for a tab opened before the switch. */
function guestToken(): string | null {
  return localStorage.getItem('gcxp_token') || sessionStorage.getItem('precheckin_jwt');
}

function authHeaders(): HeadersInit {
  const jwt = guestToken();
  return {
    'Content-Type': 'application/json',
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function getPreCheckInState(): Promise<PreCheckInState> {
  return fetch(url('/api/v1/precheckin/state'), { headers: authHeaders() }).then((r) => handle<PreCheckInState>(r));
}

export function savePreCheckInDetails(payload: PreCheckInDetailsPayload): Promise<PreCheckInState> {
  return fetch(url('/api/v1/precheckin/details'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then((r) => handle<PreCheckInState>(r));
}

export function confirmPreCheckIn(acceptedTerms: boolean, signedName?: string): Promise<PreCheckInState> {
  return fetch(url('/api/v1/precheckin/confirm'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ acceptedTerms, signedName }),
  }).then((r) => handle<PreCheckInState>(r));
}

export interface ReservationDocument {
  id: number;
  fileName: string;
  documentType?: string | null;
  verificationStatus: string;
  locked: boolean;
  receivedAt: string;
  viewUrl?: string | null;
  /** The room it is filed to; for a room the booker manages, that room's label. */
  reservationId?: string | null;
  roomLabel?: string | null;
}

/** The rooms a guest can file documents for: their own, and those they manage as the main booker. */
export interface DocumentRoom {
  reservationId: string;
  label: string | null;
  own: boolean;
}

export function getDocumentRooms(): Promise<DocumentRoom[]> {
  return fetch(url('/api/v1/precheckin/documents/rooms'), { headers: authHeaders() }).then((r) => handle<DocumentRoom[]>(r));
}

export function moveDocument(id: number, targetReservationId: string): Promise<void> {
  return fetch(url(`/api/v1/precheckin/documents/${id}/move`), {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetReservationId }),
  }).then(async (r) => {
    if (!r.ok) {
      const body = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
      throw new Error(body.error || `HTTP ${r.status}`);
    }
  });
}

export function getDocuments(): Promise<ReservationDocument[]> {
  return fetch(url('/api/v1/precheckin/documents'), { headers: authHeaders() }).then((r) => handle<ReservationDocument[]>(r));
}

export function uploadDocument(file: File, documentType: string, forReservationId?: string): Promise<ReservationDocument> {
  const jwt = guestToken();
  const form = new FormData();
  form.append('file', file);
  form.append('documentType', documentType);
  if (forReservationId) form.append('forReservationId', forReservationId);
  // Note: no Content-Type header — the browser sets the multipart boundary.
  return fetch(url('/api/v1/precheckin/documents'), {
    method: 'POST',
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    body: form,
  }).then((r) => handle<ReservationDocument>(r));
}

export function deleteDocument(id: number): Promise<void> {
  return fetch(url(`/api/v1/precheckin/documents/${id}`), {
    method: 'DELETE',
    headers: authHeaders(),
  }).then(async (r) => {
    if (!r.ok && r.status !== 204) {
      const body = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
      throw new Error(body.error || `HTTP ${r.status}`);
    }
  });
}
